// src/components/events/JoinSection.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PhotoRequestModal from "./PhotoRequestModal";
import { openExternalUrl } from "../../native/runtime";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import EventGenderConfirmationModal from "./EventGenderConfirmationModal";
import useEventAudienceConfirmation from "./useEventAudienceConfirmation";

export function shouldUseParticipantRequestFlow(event) {
  return Boolean(
    event?.skill_access?.enabled ||
      event?.join_by_request ||
      (Array.isArray(event?.request_form_questions) && event.request_form_questions.length > 0)
  );
}

/**
 * Props:
 * - event: event object, includes join_by_request (bool)
 * - isParticipant: bool — already a participant?
 * - api: axios instance
 * - eventId: number|string
 * - onJoined: () => void  // callback when user becomes a participant (refresh data)
 * - onOpenChat: () => void // open event chat (existing handler)
 * - onOpenAfterJoinModal?: () => void // keep your current "You joined" flow
 */
export default function JoinSection({
  event,
  isParticipant,
  api,
  eventId,
  onJoined,
  onOpenChat,
  onOpenAfterJoinModal,
  onPaymentRequired = null,
  onCheckoutCapacityReserved = null,
  secondaryAction = null,
  primaryButtonClassName = "",
  secondaryButtonClassName = "",
  secondaryIconClassName = "",
  primaryPrefix = null,
  primaryJoinLabel = "",
}) {
  const { t } = useTranslation("events");
  const navigate = useNavigate();
  const { confirmAudience, audienceModal } = useEventAudienceConfirmation(event);
  const [loading, setLoading] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [myRequest, setMyRequest] = useState(null); // {status, age, gender, social_link} | null
  const [showWithdrawRequestModal, setShowWithdrawRequestModal] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState("");
  const [err, setErr] = useState("");
  const [showSinglesPartyModal, setShowSinglesPartyModal] = useState(false);
  const [showSkillConfirmationModal, setShowSkillConfirmationModal] = useState(false);
  const [confirmedSkillForRequest, setConfirmedSkillForRequest] = useState(false);

  // New states for dynamic form
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [formDescription, setFormDescription] = useState("");
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [photoGateTarget, setPhotoGateTarget] = useState(null); // "open_request_form" | "submit_request" | null
  const [isGenderModalOpen, setIsGenderModalOpen] = useState(false);
  const [genderSubmitting, setGenderSubmitting] = useState(false);
  const [genderError, setGenderError] = useState("");
  const [genderJoinOptions, setGenderJoinOptions] = useState({ skipForm: false });
  const hasCustomAvatarRef = useRef(null);
  const requestStatusChangedLocallyRef = useRef(false);
  const hasJoinForm =
    Array.isArray(event?.request_form_questions) && event.request_form_questions.length > 0;
  const instantJoinRequiresForm = Boolean(hasJoinForm && !event?.join_by_request);
  const skillAccess = event?.skill_access || {};
  const skillAction = String(skillAccess?.action || "");
  const skillRequiredLevel = Number(skillAccess?.required_level || event?.required_skill_level || 0);
  const skillRequiredLabel = String(
    skillAccess?.required_level_label ||
      ({ 1: "beginner", 2: "intermediate", 3: "advanced" }[skillRequiredLevel] || "")
  );
  const primaryButtonClass =
    primaryButtonClassName ||
    "bg-white text-blue-600 rounded-2xl h-[55px] font-semibold text-lg w-full";
  const secondaryActionClass =
    secondaryButtonClassName ||
    "flex shrink-0 items-center justify-center rounded-2xl h-[55px] w-[55px] bg-white text-blue-600";
  const secondaryActionIconClass = secondaryIconClassName || "w-6 h-6";
  const isConcept = event?.type === "concept";
  const joinEventLabel = primaryJoinLabel || t(
    isConcept ? "events.details.join_concept" : "join_section.request.join_event",
    { defaultValue: isConcept ? "Join concept" : "Join event" }
  );
  const requestSlotBlockMessage = (block) => {
    const code = block?.code;
    if (code === "participant_slots_full") {
      const rating = block?.participant_rating || {};
      return t("join_section.request.slots_full_description", {
        used: rating.slots_used,
        total: rating.slots_total,
        score: rating.score_percent,
      });
    }
    if (code === "participant_rating_blocked") {
      return t("join_section.request.rating_blocked_description");
    }
    return block?.error || "";
  };
  const renderPrimaryContent = (label, { withPrefix = true } = {}) => (
    <>
      {withPrefix && primaryPrefix ? primaryPrefix : null}
      <span className="truncate">{label}</span>
    </>
  );

  const setAvatarState = (value) => {
    hasCustomAvatarRef.current = value;
  };

  const openPhotoModal = (nextAction = null) => {
    setPhotoGateTarget(nextAction);
    setIsPhotoModalOpen(true);
  };

  const checkProfilePhoto = async (nextAction = null) => {
    if (hasCustomAvatarRef.current === true) {
      return true;
    }

    if (hasCustomAvatarRef.current === false) {
      openPhotoModal(nextAction);
      return false;
    }

    try {
      const { data } = await api.get("/accounts/profile/");
      const avatarUrl = data?.avatar_custom_url || null;
      const needPhoto =
        typeof data?.photo_required_now === "boolean"
          ? data.photo_required_now
          : !avatarUrl;

      if (!needPhoto && avatarUrl) {
        setAvatarState(true);
        return true;
      }

      setAvatarState(false);
      openPhotoModal(nextAction);
      return false;
    } catch (e) {
      setErr(e?.response?.data?.error || t("join_section.errors.submit_failed"));
      return false;
    }
  };

  // Fetch request status (if event is join-by-request)
  useEffect(() => {
    let cancelled = false;
    async function fetchMyRequest() {
      if (
        !event?.join_by_request &&
        !event?.is_singles_party &&
        !hasJoinForm &&
        !skillAccess?.enabled
      ) return;
      try {
        const { data } = await api.get(`/events/${eventId}/my-request/`);
        if (!cancelled && !requestStatusChangedLocallyRef.current) setMyRequest(data);
      } catch (e) {
        if (e?.response?.status === 404) {
          if (!cancelled && !requestStatusChangedLocallyRef.current) setMyRequest(null);
        } else {
          if (!cancelled) setErr(t("join_section.errors.fetch_request_status"));
        }
      }
    }
    fetchMyRequest();
    return () => { cancelled = true; };
  }, [api, event?.is_singles_party, event?.join_by_request, eventId, t, hasJoinForm, skillAccess?.enabled]);

  // Helper to load form schema/questions
  const loadFormSchema = async () => {
    setSchemaLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/events/${eventId}/participant-request-form/`);
      setQuestions(data.questions || []);
      setFormDescription(data.form_description || "");
    } catch {
      setErr(t("join_section.errors.load_form"));
      setQuestions([]);
    } finally {
      setSchemaLoading(false);
    }
  };

  const openRequestForm = async ({ skipSkillConfirmation = false } = {}) => {
    if (!(await confirmAudience())) return;
    const hasPhoto = await checkProfilePhoto("open_request_form");
    if (!hasPhoto) return;

    if (skillAction === "confirmation_required" && !skipSkillConfirmation) {
      setShowSkillConfirmationModal(true);
      return;
    }

    setShowRequestModal(true);
    setAnswers({});
    setErr("");
    await loadFormSchema();
  };

  // Direct join (for open events) — your current code
  const handleDirectJoin = async ({
    skipForm = false,
    confirmedGender = null,
    confirmGenderMismatch = false,
  } = {}) => {
    if (!(await confirmAudience())) return;
    const hasPhoto = await checkProfilePhoto("direct_join");
    if (!hasPhoto) return;

    if (
      instantJoinRequiresForm &&
      !skipForm &&
      myRequest?.status !== "approved"
    ) {
      setShowRequestModal(true);
      setAnswers({});
      setErr("");
      await loadFormSchema();
      return;
    }

    setLoading(true);
    setErr("");
    try {
      const payload = confirmedGender
        ? {
            confirmed_gender: confirmedGender,
            ...(confirmGenderMismatch ? { confirm_gender_mismatch: true } : {}),
          }
        : {};
      const { data } = await api.post(`/events/join-event/${eventId}/`, payload);
      setIsGenderModalOpen(false);
      setGenderError("");
      if (data?.payment_required) {
        if (typeof onPaymentRequired === "function") {
          await onPaymentRequired(data);
        } else if (data?.pay_url) {
          await openExternalUrl(data.pay_url);
        }
        return;
      }
      onJoined?.();
      onOpenAfterJoinModal?.(); // your current "You joined" modal
    } catch (e) {
      const code = String(e?.response?.data?.code || "").trim();
      if (
        [
          "checkout_capacity_reserved",
          "gender_segment_full",
          "seat_hold_no_capacity",
        ].includes(code) &&
        typeof onCheckoutCapacityReserved === "function"
      ) {
        setErr("");
        setIsGenderModalOpen(false);
        onCheckoutCapacityReserved(e?.response?.data || null);
        return;
      }
      if (code === "gender_confirmation_required") {
        setGenderJoinOptions({ skipForm });
        setGenderError("");
        setIsGenderModalOpen(true);
        return;
      }
      if (code === "skill_confirmation_required") {
        setShowSkillConfirmationModal(true);
        return;
      }
      if (code === "skill_request_required") {
        await openRequestForm();
        return;
      }
      if (code === "gender_mismatch_confirmation_required" && confirmedGender) {
        await handleDirectJoin({
          skipForm,
          confirmedGender,
          confirmGenderMismatch: true,
        });
        return;
      }
      const message =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        t("join_section.errors.join_failed");
      setErr(message);
      if (confirmedGender) {
        setGenderError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handlers for dynamic form
  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const renderQuestionField = (q) => {
    const baseInputClass =
      "bg-transparent border border-white rounded-md px-4 h-12 placeholder-white text-white focus:outline-none";
    const value = answers[q.id] || "";
    const options = Array.isArray(q.options) ? q.options : [];

    if (q.field_type === "long_text" || q.field_type === "textarea") {
      return (
        <textarea
          value={value}
          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
          onKeyDown={handleKeyDown}
          className="bg-transparent border border-white rounded-md px-4 py-2 min-h-[80px] placeholder-white/80 text-white focus:outline-none"
        />
      );
    }

    if (q.field_type === "select") {
      return (
        <select
          value={value}
          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
          className="bg-blue-600 border border-white rounded-md px-4 h-12 text-white focus:outline-none"
        >
          <option value="">{t("join_section.modal.select_placeholder", { defaultValue: "Choose an option" })}</option>
          {options.map((option) => (
            <option key={`${q.id}-${option}`} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={q.field_type === "number" ? "number" : q.field_type === "link" ? "url" : "text"}
        inputMode={q.field_type === "number" ? "numeric" : undefined}
        value={value}
        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
        onKeyDown={handleKeyDown}
        className={baseInputClass}
      />
    );
  };

  // Validate form before submit
  const validateForm = () => {
    for (const q of questions) {
      if (q.required) {
        const answer = answers[q.id];
        if (!answer || !answer.toString().trim()) {
          setErr(t("join_section.errors.required_fields"));
          return false;
        }
      }
    }
    return true;
  };

  const [submitLoading, setSubmitLoading] = useState(false);

  const handleSubmitRequest = async () => {
    setErr("");
    const hasPhoto = await checkProfilePhoto("submit_request");
    if (!hasPhoto) return;

    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      const payload = {
        answers: Object.fromEntries(
          Object.entries(answers).map(([questionId, value]) => [Number(questionId), value])
        ),
        ...(confirmedSkillForRequest && skillRequiredLevel
          ? { confirmed_skill_level: skillRequiredLevel }
          : {}),
      };
      const { data } = await api.post(`/events/${eventId}/participant-requests/`, payload);
      const nextStatus =
        data?.status || (event?.join_by_request || event?.is_singles_party ? "pending" : "approved");
      requestStatusChangedLocallyRef.current = true;
      setMyRequest({ status: nextStatus });
      setShowRequestModal(false);
      setPhotoGateTarget(null);
      if (nextStatus === "approved" && !event?.join_by_request && !event?.is_singles_party) {
        await handleDirectJoin({ skipForm: true });
      }
    } catch (e) {
      const code = e?.response?.data?.code;
      if (code === "photo_required" || code === "photo_change_required") {
        setAvatarState(false);
        openPhotoModal("submit_request");
      } else if (code === "skill_confirmation_required") {
        setShowRequestModal(false);
        setShowSkillConfirmationModal(true);
      } else {
        setErr(
          requestSlotBlockMessage(e?.response?.data) ||
            e?.response?.data?.detail ||
            t("join_section.errors.submit_failed")
        );
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleWithdrawRequest = async () => {
    if (!eventId || withdrawLoading) return;
    setWithdrawLoading(true);
    setWithdrawError("");
    setErr("");
    try {
      const { data } = await api.delete(`/events/${eventId}/my-request/`);
      requestStatusChangedLocallyRef.current = true;
      setMyRequest(null);
      setShowWithdrawRequestModal(false);
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: data?.message || t("join_section.request.withdraw_success"),
          },
        })
      );
    } catch (e) {
      setWithdrawError(
        e?.response?.data?.detail ||
          e?.response?.data?.error ||
          t("join_section.errors.withdraw_failed")
      );
    } finally {
      setWithdrawLoading(false);
    }
  };

  const photoModal = (
    <PhotoRequestModal
      visible={isPhotoModalOpen}
      onClose={() => {
        setIsPhotoModalOpen(false);
        setPhotoGateTarget(null);
      }}
      onUploaded={() => {
        setAvatarState(true);
        setIsPhotoModalOpen(false);
        const nextAction = photoGateTarget;
        setPhotoGateTarget(null);
        if (nextAction === "open_request_form") {
          if (skillAction === "confirmation_required" && !confirmedSkillForRequest) {
            setShowSkillConfirmationModal(true);
            return;
          }
          setShowRequestModal(true);
          setAnswers({});
          setErr("");
          void loadFormSchema();
          return;
        }
        if (nextAction === "submit_request") {
          void handleSubmitRequest();
          return;
        }
        if (nextAction === "direct_join") {
          void handleDirectJoin();
        }
      }}
    />
  );
  const genderModal = (
    <EventGenderConfirmationModal
      open={isGenderModalOpen}
      event={event}
      loading={genderSubmitting}
      error={genderError}
      onClose={() => {
        if (genderSubmitting) return;
        setIsGenderModalOpen(false);
        setGenderError("");
      }}
      onSelect={async (confirmedGender) => {
        if (genderSubmitting) return;
        setGenderSubmitting(true);
        setGenderError("");
        try {
          await handleDirectJoin({
            ...genderJoinOptions,
            confirmedGender,
          });
        } catch (error) {
          setGenderError(
            error?.response?.data?.error || t("join_section.errors.join_failed")
          );
        } finally {
          setGenderSubmitting(false);
        }
      }}
    />
  );
  const requestModal = (
    <AppModalShell
      open={showRequestModal}
      title={
        skillAccess?.enabled
          ? t("join_section.skill.request_title", { defaultValue: "Request host approval" })
          : t("join_section.modal.title")
      }
      description={
        (skillAccess?.enabled
          ? t("join_section.skill.request_description", {
              defaultValue: `This is a ${skillRequiredLabel} event. The host will review your skill information before adding you.`,
              level: skillRequiredLabel,
            })
          : "") || formDescription ||
        (questions.length > 0
          ? t("join_section.modal.description_fallback")
          : t("join_section.modal.description_no_questions"))
      }
      onClose={() => {
        if (submitLoading) return;
        setShowRequestModal(false);
        setConfirmedSkillForRequest(false);
        setErr("");
      }}
      maxWidthClassName="max-w-md"
      footer={
        <div className="flex gap-3">
          <AppModalButton
            onClick={handleSubmitRequest}
            disabled={submitLoading}
            loading={submitLoading}
            className="flex-1 text-[#2563EB]"
          >
            {submitLoading
              ? t("join_section.modal.submitting")
              : t("join_section.modal.submit")}
          </AppModalButton>
          <AppModalButton
            variant="secondary"
            onClick={() => {
              setShowRequestModal(false);
              setConfirmedSkillForRequest(false);
              setErr("");
            }}
            disabled={submitLoading}
            className="flex-1"
          >
            {t("join_section.modal.cancel")}
          </AppModalButton>
        </div>
      }
    >
      <div className="flex flex-col space-y-4">
        {schemaLoading && questions.length === 0 ? (
          <div className="text-center text-white/70">
            {t("join_section.modal.loading")}
          </div>
        ) : (
          <>
            {questions.map((q) => (
              <div key={q.id} className="flex flex-col space-y-1">
                <label className="text-sm text-white/90">
                  {q.label}
                  {q.required && <span className="text-red-300"> *</span>}
                </label>
                {renderQuestionField(q)}
              </div>
            ))}
            {questions.length === 0 && (
              <div className="text-sm text-white/85">
                {t("join_section.modal.no_questions_info")}
              </div>
            )}
          </>
        )}
      </div>
      {err ? <div className="mt-3 text-center text-white/90">{err}</div> : null}
    </AppModalShell>
  );
  const skillConfirmationModal = (
    <AppModalShell
      open={showSkillConfirmationModal}
      title={t("join_section.skill.confirm_title", {
        defaultValue: `${skillRequiredLabel || "Selected"} level event`,
        level: skillRequiredLabel,
      })}
      description={t("join_section.skill.confirm_description", {
        defaultValue:
          "Your level for this sport is not calibrated yet. Confirm that you can play at the required level. Your confirmation will be sent to the host for review.",
        level: skillRequiredLabel,
      })}
      onClose={() => {
        setShowSkillConfirmationModal(false);
        setConfirmedSkillForRequest(false);
      }}
      maxWidthClassName="max-w-md"
      footer={
        <div className="flex gap-3">
          <AppModalButton
            onClick={() => {
              setConfirmedSkillForRequest(true);
              setShowSkillConfirmationModal(false);
              void openRequestForm({ skipSkillConfirmation: true });
            }}
            className="flex-1 text-[#2563EB]"
          >
            {t("join_section.skill.confirm_and_request", {
              defaultValue: "Confirm and request",
            })}
          </AppModalButton>
          <AppModalButton
            variant="secondary"
            onClick={() => {
              setShowSkillConfirmationModal(false);
              setConfirmedSkillForRequest(false);
            }}
            className="flex-1"
          >
            {t("join_section.modal.cancel")}
          </AppModalButton>
        </div>
      }
    >
      <div className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm leading-5 text-white/85">
        {t("join_section.skill.confirm_note", {
          defaultValue: "This confirmation does not calibrate or change your global skill rating.",
        })}
      </div>
    </AppModalShell>
  );
  const withdrawRequestModal = (
    <AppModalShell
      open={showWithdrawRequestModal}
      title={t("join_section.request.withdraw_title")}
      description={t("join_section.request.withdraw_description")}
      onClose={() => {
        if (!withdrawLoading) {
          setShowWithdrawRequestModal(false);
          setWithdrawError("");
        }
      }}
      closeOnBackdropClick={!withdrawLoading}
      maxWidthClassName="max-w-md"
      footer={
        <div className="space-y-3">
          <AppModalButton
            variant="danger"
            onClick={handleWithdrawRequest}
            disabled={withdrawLoading}
            loading={withdrawLoading}
          >
            {withdrawLoading
              ? t("join_section.request.withdrawing")
              : t("join_section.request.withdraw_confirm")}
          </AppModalButton>
          <AppModalButton
            variant="secondary"
            onClick={() => {
              setShowWithdrawRequestModal(false);
              setWithdrawError("");
            }}
            disabled={withdrawLoading}
          >
            {t("join_section.request.keep_request")}
          </AppModalButton>
        </div>
      }
    >
      <div className="rounded-[18px] border border-white/10 bg-white/[0.06] px-4 py-3 text-sm leading-5 text-white/78">
        {t("join_section.request.withdraw_no_penalty")}
      </div>
      {withdrawError ? (
        <div
          role="alert"
          className="mt-3 rounded-[18px] border border-rose-300/20 bg-rose-400/10 px-4 py-3 text-sm leading-5 text-rose-100"
        >
          {withdrawError}
        </div>
      ) : null}
    </AppModalShell>
  );
  const renderPrimaryActionRow = (primaryNode) => {
    const hasSecondary =
      secondaryAction &&
      typeof secondaryAction === "object" &&
      typeof secondaryAction.onClick === "function";

    if (!hasSecondary) {
      return primaryNode;
    }

    return (
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">{primaryNode}</div>
        <button
          type="button"
          onClick={secondaryAction.onClick}
          className={secondaryActionClass}
          aria-label={secondaryAction.ariaLabel || secondaryAction.label || "Secondary action"}
          title={secondaryAction.label || undefined}
        >
          {secondaryAction.iconSrc ? (
            <img
              src={secondaryAction.iconSrc}
              alt=""
              aria-hidden="true"
              className={secondaryActionIconClass}
            />
          ) : (
            <span className="text-[12px] font-semibold px-2 text-center leading-4">
              {secondaryAction.label || ""}
            </span>
          )}
        </button>
      </div>
    );
  };

  const singlesEligibility = event?.singles_party_eligibility || {};
  const singlesRequestStatus = myRequest?.status || singlesEligibility?.request_status || "none";
  const singlesMatchCount = Number(singlesEligibility?.match_count || 0);
  const singlesMatchThreshold = Number(
    singlesEligibility?.match_threshold || event?.singles_party_match_threshold || 10
  );
  const singlesDirectJoinUnlocked = Boolean(singlesEligibility?.direct_join_unlocked);
  const singlesProfileActive = Boolean(singlesEligibility?.active_profile);

  const openSinglesFromParty = () => {
    try {
      window.sessionStorage.setItem(
        "vg_singles_party_return_path",
        window.location.pathname + window.location.search
      );
    } catch {}
    setShowSinglesPartyModal(false);
    navigate("/menu/singles");
  };

  const singlesPartyModal = (
    <AppModalShell
      open={showSinglesPartyModal}
      title={t("join_section.singles_party.title")}
      description={t("join_section.singles_party.description")}
      onClose={() => setShowSinglesPartyModal(false)}
      maxWidthClassName="max-w-md"
      footer={
        !singlesProfileActive ? (
          <AppModalButton onClick={openSinglesFromParty} className="w-full text-[#2563EB]">
            {t("join_section.singles_party.create_profile")}
          </AppModalButton>
        ) : singlesDirectJoinUnlocked || singlesRequestStatus === "approved" ? (
          <div className="flex w-full gap-3">
            <AppModalButton
              onClick={() => {
                setShowSinglesPartyModal(false);
                void handleDirectJoin();
              }}
              disabled={loading}
              loading={loading}
              className="flex-1 text-[#2563EB]"
            >
              {t("join_section.singles_party.join_now")}
            </AppModalButton>
            <AppModalButton variant="secondary" onClick={() => setShowSinglesPartyModal(false)} className="flex-1">
              {t("join_section.singles_party.not_now")}
            </AppModalButton>
          </div>
        ) : singlesRequestStatus === "pending" ? (
          <div className="flex w-full gap-3">
            <AppModalButton disabled className="flex-1 text-[#2563EB]">
              {t("join_section.singles_party.request_sent")}
            </AppModalButton>
            <AppModalButton variant="secondary" onClick={openSinglesFromParty} className="flex-1">
              {t("join_section.singles_party.open_singles")}
            </AppModalButton>
          </div>
        ) : (
          <div className="flex w-full gap-3">
            <AppModalButton
              onClick={() => {
                setShowSinglesPartyModal(false);
                void openRequestForm();
              }}
              className="flex-1 text-[#2563EB]"
            >
              {t("join_section.singles_party.send_request")}
            </AppModalButton>
            <AppModalButton variant="secondary" onClick={openSinglesFromParty} className="flex-1">
              {t("join_section.singles_party.open_singles")}
            </AppModalButton>
          </div>
        )
      }
    >
      <div className="space-y-4 text-white/85">
        {!singlesProfileActive ? (
          <div className="rounded-2xl border border-sky-300/20 bg-sky-400/10 px-4 py-3 text-sm leading-5">
            {t("join_section.singles_party.profile_required", {
              count: singlesMatchThreshold,
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-white/65">{t("join_section.singles_party.your_matches")}</span>
              <span className="text-xl font-semibold text-white">{singlesMatchCount}/{singlesMatchThreshold}</span>
            </div>
            <p className="mt-2 text-sm leading-5 text-white/65">
              {singlesDirectJoinUnlocked || singlesRequestStatus === "approved"
                ? t("join_section.singles_party.direct_unlocked")
                : singlesRequestStatus === "pending"
                  ? t("join_section.singles_party.pending")
                  : t("join_section.singles_party.two_paths", { count: singlesMatchThreshold })}
            </p>
          </div>
        )}
      </div>
    </AppModalShell>
  );

  // UI branching
  if (isParticipant) {
    return (
      <>
        <div className="vg-join">
          <button className="vg-btn vg-btn--primary" onClick={onOpenChat}>
            {t("join_section.open_chat_button")}
          </button>
        </div>
        {photoModal}
        {genderModal}
      </>
    );
  }

  if (event?.is_singles_party) {
    return (
      <div className="vg-join">
        {renderPrimaryActionRow(
          <button
            className={primaryButtonClass}
            onClick={async () => {
              if (await confirmAudience()) setShowSinglesPartyModal(true);
            }}
          >
            {renderPrimaryContent(t("join_section.singles_party.open_join"))}
          </button>
        )}
        {err ? <div className="vg-error">{err}</div> : null}
        {singlesPartyModal}
        {audienceModal}
        {requestModal}
        {withdrawRequestModal}
        {skillConfirmationModal}
        {photoModal}
        {genderModal}
      </div>
    );
  }

  const skillRequestMode = Boolean(
    skillAccess?.enabled &&
      (skillAction === "request_required" ||
        skillAction === "confirmation_required" ||
        skillAccess?.reason === "host_approved_override" ||
        myRequest?.status === "pending" ||
        myRequest?.status === "rejected")
  );

  if (event?.join_by_request || skillRequestMode) {
    // Request-only mode
    const status = myRequest?.status || "none";

    if (status === "pending") {
      const approvalBlockMessage = requestSlotBlockMessage(myRequest?.approval_block);
      const canManageApprovalBlock = ["participant_slots_full", "participant_rating_blocked"].includes(
        myRequest?.approval_block?.code
      );
      return (
        <>
          <div className="vg-join">
            {renderPrimaryActionRow(
              <button
                type="button"
                className={primaryButtonClass}
                onClick={() => {
                  setWithdrawError("");
                  setShowWithdrawRequestModal(true);
                }}
              >
                {renderPrimaryContent(t("join_section.request.withdraw"), { withPrefix: false })}
              </button>
            )}
            {err ? <div className="vg-error">{err}</div> : null}
            {approvalBlockMessage ? (
              <div className="mt-2 rounded-[16px] border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-[13px] leading-[1.4] text-amber-50">
                <div className="font-semibold">{t("join_section.request.approval_paused_title")}</div>
                <div className="mt-1">{approvalBlockMessage}</div>
                {canManageApprovalBlock ? (
                  <button
                    type="button"
                    className="mt-2 font-semibold underline underline-offset-2"
                    onClick={() => navigate("/events/participant-rating")}
                  >
                    {t("join_section.request.manage_slots")}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          {audienceModal}
          {requestModal}
          {withdrawRequestModal}
          {skillConfirmationModal}
          {photoModal}
          {genderModal}
        </>
      );
    }

    if (status === "rejected") {
      return (
        <>
          <div className="vg-join">
            {renderPrimaryActionRow(
              <button
                className={primaryButtonClass}
                onClick={openRequestForm}
              >
                {renderPrimaryContent(t("join_section.request.resubmit"))}
              </button>
            )}
            <div className="mt-2 rounded-[16px] border border-white/12 bg-white/[0.08] px-4 py-3 text-center text-[13px] leading-[1.4] text-white/76">
              {t("join_section.request.rejected_message")}
            </div>
            {err ? <div className="vg-error">{err}</div> : null}
          </div>
          {audienceModal}
          {requestModal}
          {skillConfirmationModal}
          {photoModal}
          {genderModal}
        </>
      );
    }

    if (status === "approved") {
      const joinPaymentPercent = Number(event?.host_paid_join_payment_percent);
      const isDepositJoin =
        Boolean(event?.is_payable_in_advance) &&
        Number.isFinite(joinPaymentPercent) &&
        joinPaymentPercent > 0 &&
        joinPaymentPercent < 100;
      const approvedCtaLabel =
        isDepositJoin && !event?.host_paid_allow_full_ticket_payment_on_join
        ? t("join_section.request.join_with_deposit")
        : event?.is_payable_in_advance || isConcept
          ? joinEventLabel
          : t("join_section.open_mode.join_button");

      return (
        <>
          <div className="vg-join">
            {renderPrimaryActionRow(
              <button
                className={primaryButtonClass}
                onClick={handleDirectJoin}
                disabled={loading}
              >
                {renderPrimaryContent(loading ? t("join_section.open_mode.joining") : approvedCtaLabel)}
              </button>
            )}
            {err ? <div className="vg-error">{err}</div> : null}
          </div>
          {audienceModal}
          {skillConfirmationModal}
          {photoModal}
          {genderModal}
        </>
      );
    }

    // No request yet — show "Send request" white button and modal
    return (
      <div className="vg-join">
        {renderPrimaryActionRow(
          <button
            className={primaryButtonClass}
            onClick={openRequestForm}
          >
            {renderPrimaryContent(
              skillAccess?.enabled
                ? t("join_section.skill.request_button", { defaultValue: "Request to join" })
                : joinEventLabel
            )}
          </button>
        )}
        {err ? <div className="vg-error">{err}</div> : null}

        {audienceModal}
        {requestModal}
        {skillConfirmationModal}
        {photoModal}
        {genderModal}
      </div>
    );
  }

  // Open event mode (as now)
  return (
    <>
      <div className="vg-join">
        {renderPrimaryActionRow(
          <button
            className={primaryButtonClassName || "vg-btn vg-btn--primary"}
            onClick={handleDirectJoin}
            disabled={loading}
          >
            {primaryButtonClassName
              ? renderPrimaryContent(loading ? t("join_section.open_mode.joining") : joinEventLabel)
              : (
                  loading
                    ? t("join_section.open_mode.joining")
                    : isConcept ? joinEventLabel : t("join_section.open_mode.join_button")
                )}
          </button>
        )}
        {err ? <div className="vg-error">{err}</div> : null}
      </div>
      {skillConfirmationModal}
      {audienceModal}
      {requestModal}
      {photoModal}
      {genderModal}
    </>
  );
}
